import { useState } from "react";

import { useTranslation } from "react-i18next";

import useMyCodSignature from "../../data/useMyCodSignature";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import FileUpload from "../shared/Inputs/FileUpload";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

// Your own CoD signature, so a Certificate of Destruction you submit carries your signature rather
// than one shared by the whole business. The customer-wide signature and the COD credentials stay on
// the admin CoD page - this only ever touches yours.
export default function MyCodSignatureCard() {
  const { t } = useTranslation("profile");
  const { status, loading, upload, remove } = useMyCodSignature();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(t("Signature"));

  const [toUpload, setToUpload] = useState<File[]>([]);

  const handleUpload = () => {
    if (!toUpload.length) return;

    const formData = new FormData();
    formData.append("signature", toUpload[0]);

    upload.mutate(formData, {
      onSuccess: () => {
        setToUpload([]);
        saveHandlers.onSuccess();
      },
      onError: saveHandlers.onError,
    });
  };

  return (
    <section className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900">{t("My COD Signature")}</h2>
      <p className="mt-1 text-sm text-gray-500">
        {t(
          "Certificates of Destruction you submit are signed with this. If you haven't uploaded one, the signature set up for the business is used instead."
        )}
      </p>

      {loading ? (
        <div className="mt-4">
          <LoadingWheel />
        </div>
      ) : (
        <>
          {/* Both flags are customer-wide and admin-controlled, so they are reported rather than
              offered - otherwise someone uploads a signature and can't tell why nothing is signed.
              UserOwnedCods is checked first: with it off, the apply toggle is beside the point
              because the business signature would be used regardless. */}
          {!status?.userOwnedCods ? (
            <p className="mt-4 rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              {t(
                "Per-user signatures aren't switched on for the business yet, so certificates are signed with the shared signature instead of yours. An administrator can enable this on the COD admin page."
              )}
            </p>
          ) : !status?.applySignature ? (
            <p className="mt-4 rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              {t(
                "Signing is currently switched off for the business, so this signature won't be added to certificates. An administrator can enable it on the COD admin page."
              )}
            </p>
          ) : null}

          {status?.signatureUploaded && status?.signatureBase64 && (
            <div className="mt-6">
              <span className="text-sm font-medium text-gray-700">{t("Current signature")}</span>
              <div className="mt-2 flex items-center gap-4">
                <img
                  src={`data:image/png;base64,${status.signatureBase64}`}
                  alt={t("My COD signature")}
                  className="h-24 max-w-xs object-contain border border-gray-200 rounded bg-gray-50 p-2"
                />
                <ButtonInput
                  label={t("Remove")}
                  isSubmit={false}
                  onClick={() => remove.mutate(undefined, deleteHandlers)}
                  isLoading={remove.isLoading}
                  classes="!bg-red-600 hover:!bg-red-700"
                />
              </div>
            </div>
          )}

          <div className="mt-6">
            <FileUpload
              label={status?.signatureUploaded ? t("Replace Signature") : t("Upload Signature")}
              accept=".png,.jpg,.jpeg"
              desc={t("Transparent PNG recommended")}
              updateFilesCb={(files: File[]) => setToUpload(files)}
            />
            <ButtonInput
              label={t("Upload")}
              isSubmit={false}
              onClick={handleUpload}
              disabled={!toUpload.length}
              isLoading={upload.isLoading}
              classes={undefined}
            />
          </div>
        </>
      )}
    </section>
  );
}
